import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Space, Dropdown, Modal, message, Switch, Tooltip } from 'antd';
import { MoreOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse, faCheckCircle, faBan, faStar } from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../../layouts/AdminLayout';
import OnDemandStorageUnitService from '../../../services/OnDemandStorageUnitService';
import {
  PageContainer,
  TopBar,
  SearchInput,
  ActionButton,
  StyledTable,
  StatusTag,
  StyledModal,
  FormSection,
  EmptyState,
  StatsRow,
  StatCard,
} from './styled';

const StorageUnitsPage = () => {
  const { setPageHeader } = usePageHeader();

  const [storageUnits, setStorageUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    setPageHeader({ title: 'Storage Units', showBack: false });
  }, [setPageHeader]);

  const fetchStorageUnits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await OnDemandStorageUnitService.getStorageUnits({ search: searchText });
      if (response.success) {
        setStorageUnits(response.data);
      }
    } catch (error) {
      console.error('Error fetching storage units:', error);
      message.error('Failed to load storage units');
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  useEffect(() => {
    fetchStorageUnits();
  }, [fetchStorageUnits]);
  const handleCreate = () => {
    setEditingUnit(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUnit(record);
    form.setFieldsValue({
      name: record.name,
      code: record.code,
      isPrimary: record.isPrimary,
      street: record.address?.street,
      city: record.address?.city,
      state: record.address?.state,
      zipCode: record.address?.zipCode,
      latitude: record.coordinates?.latitude,
      longitude: record.coordinates?.longitude,
      accessInstructions: record.accessInstructions,
      accessCode: record.accessCode,
      contactName: record.contactName,
      contactPhone: record.contactPhone,
      contactEmail: record.contactEmail,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        name: values.name,
        code: values.code,
        isPrimary: values.isPrimary || false,
        address: {
          street: values.street,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
        },
        coordinates: {
          latitude: values.latitude ? parseFloat(values.latitude) : undefined,
          longitude: values.longitude ? parseFloat(values.longitude) : undefined,
        },
        accessInstructions: values.accessInstructions,
        accessCode: values.accessCode,
        contactName: values.contactName,
        contactPhone: values.contactPhone,
        contactEmail: values.contactEmail,
      };

      if (editingUnit) {
        const response = await OnDemandStorageUnitService.updateStorageUnit(editingUnit._id, payload);
        if (response.success) {
          message.success('Storage unit updated');
        } else if (response.message) {
          message.error(response.message);
        }
      } else {
        const response = await OnDemandStorageUnitService.createStorageUnit(payload);
        if (response.success) {
          message.success('Storage unit created');
        } else if (response.message) {
          message.error(response.message);
        }
      }

      setModalVisible(false);
      fetchStorageUnits();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (!error.errorFields) {
        message.error('Failed to save storage unit');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await OnDemandStorageUnitService.deleteStorageUnit(id);
      if (response.success) {
        message.success('Storage unit deactivated');
        fetchStorageUnits();
      }
    } catch (error) {
      message.error('Failed to deactivate storage unit');
    }
  };

  const activeUnits = storageUnits.filter(u => u.isActive);
  const primaryUnits = storageUnits.filter(u => u.isPrimary && u.isActive);

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (code) => <strong>{code}</strong>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Address',
      key: 'address',
      render: (_, record) => {
        const addr = record.address;
        if (!addr?.city) return '-';
        return `${addr.city}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      },
    },
    {
      title: 'Primary',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: 80,
      align: 'center',
      render: (isPrimary) => isPrimary ? (
        <Tooltip title="Primary storage">
          <FontAwesomeIcon icon={faStar} style={{ color: '#faad14' }} />
        </Tooltip>
      ) : null,
    },
    {
      title: 'HCWs',
      key: 'hcws',
      width: 70,
      align: 'center',
      render: (_, record) => record.approvedHCWs?.length || 0,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <StatusTag color={isActive ? 'success' : 'default'}>
          <FontAwesomeIcon icon={isActive ? faCheckCircle : faBan} style={{ marginRight: 4 }} />
          {isActive ? 'Active' : 'Inactive'}
        </StatusTag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const items = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEdit(record),
          },
        ];
        if (record.isActive) {
          items.push({
            key: 'deactivate',
            icon: <StopOutlined />,
            label: 'Deactivate',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Deactivate this storage unit?',
                onOk: () => handleDelete(record._id),
              });
            },
          });
        }
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <ActionButton
              type="text"
              icon={<MoreOutlined style={{ fontSize: 20 }} />}
              style={{ border: 'none', boxShadow: 'none' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <div className="header-animate-in">
        <StatsRow>
          <StatCard $bgColor="#e6f7ff" $iconColor="#1890ff">
            <div className="stat-icon"><FontAwesomeIcon icon={faWarehouse} /></div>
            <div className="stat-info">
              <div className="stat-value">{storageUnits.length}</div>
              <div className="stat-label">Total Units</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#f6ffed" $iconColor="#52c41a">
            <div className="stat-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
            <div className="stat-info">
              <div className="stat-value">{activeUnits.length}</div>
              <div className="stat-label">Active</div>
            </div>
          </StatCard>
          <StatCard $bgColor="#fff7e6" $iconColor="#faad14">
            <div className="stat-icon"><FontAwesomeIcon icon={faStar} /></div>
            <div className="stat-info">
              <div className="stat-value">{primaryUnits.length}</div>
              <div className="stat-label">Primary Units</div>
            </div>
          </StatCard>
        </StatsRow>

        <TopBar>
          <SearchInput
            placeholder="Search by name, code, or city..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <ActionButton type="primary" onClick={handleCreate}>
            + Add Storage Unit
          </ActionButton>
        </TopBar>
      </div>

      <div className="table-animate-in">
        {storageUnits.length === 0 && !loading ? (
          <EmptyState>
            <div className="icon"><FontAwesomeIcon icon={faWarehouse} /></div>
            <div className="title">No Storage Units Yet</div>
            <div className="description">Create your first storage unit to start managing kit locations</div>
            <ActionButton ghost onClick={handleCreate}>+ Add Storage Unit</ActionButton>
          </EmptyState>
        ) : (
          <StyledTable
            columns={columns}
            dataSource={storageUnits}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 900, y: 'calc(100vh - 380px)' }}
            sticky
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} storage units`,
            }}
          />
        )}
      </div>

      <StyledModal
        title={editingUnit ? 'Edit Storage Unit' : 'Create Storage Unit'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        width={640}
        okText={editingUnit ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical">
          <FormSection>
            <div className="section-title">Basic Information</div>
            <Form.Item name="name" label="Unit Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input placeholder="e.g., Dallas Main Storage" />
            </Form.Item>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Code is required' }]} style={{ flex: 1 }}>
                <Input placeholder="e.g., DAL-01" style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Space>
            <Form.Item name="isPrimary" label="Primary Storage" valuePropName="checked">
              <Switch checkedChildren="Primary" unCheckedChildren="Backup" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <div className="section-title">Address</div>
            <Form.Item name="street" label="Street">
              <Input placeholder="123 Main St" />
            </Form.Item>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="city" label="City" style={{ flex: 2 }}>
                <Input placeholder="Dallas" />
              </Form.Item>
              <Form.Item name="state" label="State" style={{ flex: 1 }}>
                <Input placeholder="TX" />
              </Form.Item>
              <Form.Item name="zipCode" label="Zip" style={{ flex: 1 }}>
                <Input placeholder="75201" />
              </Form.Item>
            </Space>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="latitude" label="Latitude" style={{ flex: 1 }}>
                <Input placeholder="32.7767" />
              </Form.Item>
              <Form.Item name="longitude" label="Longitude" style={{ flex: 1 }}>
                <Input placeholder="-96.7970" />
              </Form.Item>
            </Space>
          </FormSection>

          <FormSection>
            <div className="section-title">Access Information</div>
            <Form.Item name="accessInstructions" label="Access Instructions">
              <Input.TextArea rows={3} placeholder="Gate code, key location, special instructions..." />
            </Form.Item>
            <Form.Item name="accessCode" label="Access Code">
              <Input.Password placeholder="Gate code / entry combination" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <div className="section-title">Contact</div>
            <Space style={{ width: '100%' }} size={16}>
              <Form.Item name="contactName" label="Contact Name" style={{ flex: 1 }}>
                <Input placeholder="John Doe" />
              </Form.Item>
              <Form.Item name="contactPhone" label="Phone" style={{ flex: 1 }}>
                <Input placeholder="(555) 123-4567" />
              </Form.Item>
            </Space>
            <Form.Item name="contactEmail" label="Email">
              <Input placeholder="contact@example.com" />
            </Form.Item>
          </FormSection>
        </Form>
      </StyledModal>
    </PageContainer>
  );
};

export default StorageUnitsPage;
